import type { SQLiteDatabase } from 'expo-sqlite';

import type { GoalPriority, GoalStatus } from '@/db/types';
import { parseLocalDate } from '@/lib/dates';
import { addGoal, type NewGoalInput } from '@/lib/goals';

/**
 * Bulk import for goals — same pattern as inventory-csv. Tolerant column
 * mapper, validates rows, inserts the valid ones inside a transaction.
 *
 * Required: title. Optional: why, category, target date, est cost,
 * status (someday / planning / in-progress / done), priority (high /
 * medium / low or 1 / 2 / 3), notes.
 */

export interface GoalsCsvImportResult {
  inserted: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

const COLUMN_ALIASES: Record<keyof RawRow, string[]> = {
  title: ['title', 'goal', 'name', 'item'],
  why: ['why', 'reason', 'motivation'],
  category: ['category', 'type'],
  target_date: ['target date', 'target', 'date', 'due', 'when'],
  est_cost: ['est cost', 'estimated cost', 'cost', 'budget', 'price'],
  status: ['status', 'state'],
  priority: ['priority', 'pri'],
  notes: ['notes', 'description', 'comments'],
};

interface RawRow {
  title?: string;
  why?: string;
  category?: string;
  target_date?: string;
  est_cost?: string;
  status?: string;
  priority?: string;
  notes?: string;
}

export interface GoalsCsvPreviewRow {
  index: number;
  ok: boolean;
  reason?: string;
  parsed?: Omit<NewGoalInput, 'propertyId'>;
}

export function parseGoalsCsv(text: string): GoalsCsvPreviewRow[] {
  const rows = parseCsv(text);
  if (rows.length === 0) return [];
  const header = rows[0].map((c) => normalizeKey(c));
  const colMap = mapColumns(header);

  const out: GoalsCsvPreviewRow[] = [];
  for (let i = 1; i < rows.length; i++) {
    const cells = rows[i];
    if (cells.length === 0 || cells.every((c) => c.trim() === '')) continue;
    const raw: RawRow = {};
    for (const [key, idx] of Object.entries(colMap)) {
      if (idx == null) continue;
      raw[key as keyof RawRow] = cells[idx]?.trim();
    }
    const title = raw.title?.trim();
    if (!title) {
      out.push({ index: i, ok: false, reason: 'Missing title' });
      continue;
    }
    out.push({
      index: i,
      ok: true,
      parsed: {
        title,
        why: raw.why?.trim() || null,
        category: raw.category?.trim() || null,
        target_date: parseDate(raw.target_date),
        est_cost_cents: parseCents(raw.est_cost),
        status: parseStatus(raw.status),
        priority: parsePriority(raw.priority),
        notes: raw.notes?.trim() || null,
      },
    });
  }
  return out;
}

export async function importGoalsCsv(
  db: SQLiteDatabase,
  propertyId: string,
  rows: GoalsCsvPreviewRow[]
): Promise<GoalsCsvImportResult> {
  const result: GoalsCsvImportResult = { inserted: 0, skipped: 0, errors: [] };
  await db.withTransactionAsync(async () => {
    for (const r of rows) {
      if (!r.ok || !r.parsed) {
        result.skipped += 1;
        if (r.reason) result.errors.push({ row: r.index, message: r.reason });
        continue;
      }
      try {
        await addGoal(db, { ...r.parsed, propertyId });
        result.inserted += 1;
      } catch (err) {
        result.skipped += 1;
        result.errors.push({
          row: r.index,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    }
  });
  return result;
}

// --- parsing helpers (duplicated from inventory-csv on purpose — keeps
// each importer self-contained so future schema drift in one can't break
// the other) -------------------------------------------------------------

function normalizeKey(s: string): string {
  return s.toLowerCase().replace(/[\s_\-]+/g, ' ').trim();
}

function mapColumns(header: string[]): Partial<Record<keyof RawRow, number>> {
  const out: Partial<Record<keyof RawRow, number>> = {};
  for (const [key, aliases] of Object.entries(COLUMN_ALIASES) as [keyof RawRow, string[]][]) {
    for (const alias of aliases) {
      const idx = header.indexOf(alias);
      if (idx >= 0) { out[key] = idx; break; }
    }
  }
  return out;
}

function parseDate(raw: string | undefined): number | null {
  if (!raw) return null;
  const t = parseLocalDate(raw);
  return isNaN(t) ? null : t;
}

function parseCents(raw: string | undefined): number | null {
  if (!raw) return null;
  const cleaned = raw.replace(/[$,\s]/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : Math.round(n * 100);
}

function parseStatus(raw: string | undefined): GoalStatus {
  if (!raw) return 'someday';
  const v = raw.trim().toLowerCase();
  if (v.includes('progress') || v === 'wip' || v === 'doing') return 'in-progress';
  if (v.startsWith('plan')) return 'planning';
  if (v === 'done' || v === 'complete' || v === 'completed') return 'done';
  return 'someday';
}

function parsePriority(raw: string | undefined): GoalPriority {
  if (!raw) return 2;
  const v = raw.trim().toLowerCase();
  if (v === '1' || v.startsWith('high') || v === 'h') return 1;
  if (v === '3' || v.startsWith('low') || v === 'l') return 3;
  return 2;
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      cell += c; i += 1; continue;
    }
    if (c === '"') { inQuotes = true; i += 1; continue; }
    if (c === ',') { row.push(cell); cell = ''; i += 1; continue; }
    if (c === '\r') { i += 1; continue; }
    if (c === '\n') {
      row.push(cell); cell = '';
      rows.push(row); row = [];
      i += 1; continue;
    }
    cell += c; i += 1;
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell); rows.push(row);
  }
  return rows;
}
